import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Overline, Reveal } from "@/components/Reveal";
import Turnstile from "@/components/Turnstile";
import PrivacyNote from "@/components/PrivacyNote";
import { API, NEIGHBOURHOODS } from "@/data/campaign";

const TABS = [
  { id: "volunteer", label: "Volunteer" },
  { id: "lawn_sign", label: "Lawn Sign" },
  { id: "updates", label: "Get Updates" },
  { id: "priority", label: "Share Your Priority" },
];

const VOLUNTEER_INTERESTS = ["Door knocking", "Phone calls", "Lawn signs", "Events", "Data entry", "Other"];

const inputCls =
  "w-full border border-pine-700/25 bg-paper px-4 py-3.5 text-base text-ink placeholder:text-ink/40 focus:border-terra-500 focus:outline-none";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/60">{label}</span>
    {children}
  </label>
);

const NeighbourhoodSelect = ({ value, onChange, testid }) => (
  <select data-testid={testid} value={value} onChange={onChange} className={inputCls}>
    <option value="">Select your neighbourhood</option>
    {NEIGHBOURHOODS.map((n) => (
      <option key={n.name} value={n.name}>{n.name}</option>
    ))}
    <option value="Other Ward 11">Other Ward 11</option>
  </select>
);

const AVAILABILITY = ["Weekday daytime", "Weekday evenings", "Weekends", "Flexible"];
const TOPICS = ["Traffic", "Development", "Taxes", "Safety", "Heritage", "Other"];

const EMPTY_FORMS = {
  volunteer: { first_name: "", last_name: "", email: "", phone: "", address: "", city: "", postal_code: "", neighbourhood: "", availability: "", notes: "" },
  lawn_sign: { first_name: "", last_name: "", email: "", phone: "", street_address: "", city: "", postal_code: "", neighbourhood: "", quantity: 1, placement_notes: "" },
  updates: { first_name: "", last_name: "", email: "", neighbourhood: "" },
  priority: { name: "", email: "", phone: "", neighbourhood: "", topic: "", message: "" },
};

const ENDPOINTS = { volunteer: "volunteers", lawn_sign: "lawn-signs", updates: "updates", priority: "priorities" };

const GetInvolved = () => {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === params.get("tab")) ? params.get("tab") : "volunteer";
  const [forms, setForms] = useState(EMPTY_FORMS);
  const [interests, setInterests] = useState([]);
  const [sending, setSending] = useState(false);
  const [tsToken, setTsToken] = useState("");
  const tsReset = useRef(null);
  const form = forms[tab];

  const set = (key) => (e) => setForms((f) => ({ ...f, [tab]: { ...f[tab], [key]: e.target.value } }));

  const submit = async (e) => {
    e.preventDefault();
    if (!tsToken) return;
    setSending(true);
    try {
      const payload = { ...form, turnstile_token: tsToken };
      if (tab === "volunteer") payload.interests = interests;
      if (tab === "lawn_sign") payload.quantity = Number(form.quantity) || 1;
      const { data } = await axios.post(`${API}/forms/${ENDPOINTS[tab]}`, payload);
      toast.success("Thank you — your submission has been received.", {
        description:
          tab === "updates" && data.existing
            ? "You're already on the updates list."
            : "Amin’s campaign team will be in touch soon.",
      });
      setForms((f) => ({ ...f, [tab]: EMPTY_FORMS[tab] }));
      setInterests([]);
      tsReset.current?.();
      setTsToken("");
    } catch (err) {
      tsReset.current?.();
      setTsToken("");
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Could not send — your entries are still here. Please try again."
      );
    } finally {
      setSending(false);
    }
  };

  const toggleInterest = (i) =>
    setInterests((list) => (list.includes(i) ? list.filter((x) => x !== i) : [...list, i]));

  const intros = {
    volunteer: "Knock on doors, make calls, help at events — whatever time you can give moves Ward 11 forward.",
    lawn_sign: "Show your neighbours where you stand. We’ll deliver and install a sign on your lawn — free.",
    updates: "Campaign news, event invitations, and progress on the six commitments — straight to your inbox.",
    priority: "Speeding on your street? A development you’re worried about? Tell Amin directly — he reads every one.",
  };

  return (
    <main data-testid="get-involved-page" className="pt-28 lg:pt-32">
      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        <Reveal>
          <Overline>Join the Effort</Overline>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            Help build a <span className="italic text-terra-500">stronger Ward 11.</span>
          </h1>
        </Reveal>

        <div className="mt-14 grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="flex flex-col border-t border-pine-700/15" data-testid="involve-tabs">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  data-testid={`involve-tab-${t.id}`}
                  onClick={() => setParams({ tab: t.id })}
                  className={`border-b border-pine-700/15 px-4 py-5 text-left font-serif text-xl font-medium tracking-tight transition-colors ${
                    tab === t.id ? "bg-pine-900 text-paper" : "hover:bg-parch"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <p className="mt-6 text-sm leading-relaxed text-ink/70">{intros[tab]}</p>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-8">
            <form
              onSubmit={submit}
              data-testid={`involve-form-${tab}`}
              className="border border-pine-700/15 bg-white/60 p-6 sm:p-10"
            >
              <h2 className="font-serif text-2xl font-medium tracking-tight">
                {TABS.find((t) => t.id === tab).label}
              </h2>
              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                {tab === "priority" ? (
                  <Field label="Your Name *">
                    <input required data-testid="priority-name-input" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
                  </Field>
                ) : (
                  <>
                    <Field label="First Name *">
                      <input required data-testid={`${tab}-first-name-input`} value={form.first_name} onChange={set("first_name")} className={inputCls} placeholder="First name" />
                    </Field>
                    <Field label="Last Name *">
                      <input required data-testid={`${tab}-last-name-input`} value={form.last_name} onChange={set("last_name")} className={inputCls} placeholder="Last name" />
                    </Field>
                  </>
                )}
                <Field label="Email *">
                  <input required type="email" data-testid={`${tab}-email-input`} value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
                </Field>
                {tab !== "updates" && (
                  <Field label={tab === "lawn_sign" ? "Phone *" : "Phone"}>
                    <input required={tab === "lawn_sign"} data-testid={`${tab}-phone-input`} value={form.phone} onChange={set("phone")} className={inputCls} placeholder="416-555-0100" />
                  </Field>
                )}
                <Field label="Neighbourhood">
                  <NeighbourhoodSelect testid={`${tab}-neighbourhood-select`} value={form.neighbourhood} onChange={set("neighbourhood")} />
                </Field>
                {tab === "volunteer" && (
                  <>
                    <div className="sm:col-span-2">
                      <Field label="Address">
                        <input data-testid="volunteer-address-input" value={form.address} onChange={set("address")} className={inputCls} placeholder="Street address" />
                      </Field>
                    </div>
                    <Field label="City">
                      <input data-testid="volunteer-city-input" value={form.city} onChange={set("city")} className={inputCls} placeholder="Mississauga" />
                    </Field>
                    <Field label="Postal Code">
                      <input data-testid="volunteer-postal-input" value={form.postal_code} onChange={set("postal_code")} className={inputCls} placeholder="L5M 1A1" />
                    </Field>
                    <Field label="Availability">
                      <select data-testid="volunteer-availability-select" value={form.availability} onChange={set("availability")} className={inputCls}>
                        <option value="">Select availability</option>
                        {AVAILABILITY.map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    </Field>
                  </>
                )}
                {tab === "lawn_sign" && (
                  <>
                    <div className="sm:col-span-2">
                      <Field label="Street Address (for sign delivery) *">
                        <input required data-testid="lawn_sign-address-input" value={form.street_address} onChange={set("street_address")} className={inputCls} placeholder="Street address, Ward 11" />
                      </Field>
                    </div>
                    <Field label="City *">
                      <input required data-testid="lawn_sign-city-input" value={form.city} onChange={set("city")} className={inputCls} placeholder="Mississauga" />
                    </Field>
                    <Field label="Postal Code *">
                      <input required data-testid="lawn_sign-postal-input" value={form.postal_code} onChange={set("postal_code")} className={inputCls} placeholder="L5M 1A1" />
                    </Field>
                    <Field label="Number of Signs">
                      <input type="number" min="1" max="10" data-testid="lawn_sign-quantity-input" value={form.quantity} onChange={set("quantity")} className={inputCls} />
                    </Field>
                  </>
                )}
                {tab === "priority" && (
                  <Field label="Topic">
                    <select data-testid="priority-topic-select" value={form.topic} onChange={set("topic")} className={inputCls}>
                      <option value="">Select a topic</option>
                      {TOPICS.map((t) => (
                        <option key={t} value={t.toLowerCase()}>{t}</option>
                      ))}
                    </select>
                  </Field>
                )}
              </div>

              {tab === "volunteer" && (
                <div className="mt-6">
                  <span className="mb-3 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/60">
                    I can help with
                  </span>
                  <div className="flex flex-wrap gap-2" data-testid="volunteer-interests">
                    {VOLUNTEER_INTERESTS.map((i) => (
                      <button
                        type="button"
                        key={i}
                        data-testid={`interest-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        onClick={() => toggleInterest(i)}
                        className={`border px-4 py-3 text-sm font-semibold transition-colors ${
                          interests.includes(i)
                            ? "border-pine-700 bg-pine-700 text-paper"
                            : "border-pine-700/25 text-ink/70 hover:border-terra-500 hover:text-terra-500"
                        }`}
                      >
                        {i}
                      </button>
                    ))}
                  </div>
                  <div className="mt-6">
                    <Field label="Notes">
                      <textarea rows={3} data-testid="volunteer-notes-input" value={form.notes} onChange={set("notes")} className={inputCls} placeholder="Anything we should know? (optional)" />
                    </Field>
                  </div>
                </div>
              )}

              {tab === "lawn_sign" && (
                <div className="mt-6">
                  <Field label="Placement Notes">
                    <textarea rows={3} data-testid="lawn_sign-placement-input" value={form.placement_notes} onChange={set("placement_notes")} className={inputCls} placeholder="e.g. near the front walkway (optional)" />
                  </Field>
                </div>
              )}

              {tab === "priority" && (
                <div className="mt-6">
                  <Field label="The issue that matters most on your street *">
                    <textarea
                      required
                      rows={4}
                      data-testid="priority-message-input"
                      value={form.message}
                      onChange={set("message")}
                      className={inputCls}
                      placeholder="Describe the issue, the street, and what you'd like to see done…"
                    />
                  </Field>
                </div>
              )}

              <div className="mt-8">
                <Turnstile action={tab} onToken={setTsToken} resetRef={tsReset} />
              </div>

              <button
                type="submit"
                disabled={sending || !tsToken}
                data-testid={`${tab}-submit-button`}
                className="mt-8 flex w-full items-center justify-center gap-2 bg-pine-700 px-7 py-4 text-sm font-semibold text-paper transition-colors hover:bg-terra-500 disabled:opacity-60 sm:w-auto"
              >
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                {tab === "volunteer" && "Sign Me Up to Volunteer"}
                {tab === "lawn_sign" && "Request My Lawn Sign"}
                {tab === "updates" && "Subscribe to Updates"}
                {tab === "priority" && "Send My Priority to Amin"}
              </button>
              <PrivacyNote />
            </form>
          </Reveal>
        </div>

        <Reveal className="mt-16 border-t border-pine-700/15 pt-10 text-center">
          <Link
            to="/questions"
            data-testid="questions-link"
            className="group inline-flex items-center gap-2 font-serif text-xl font-medium tracking-tight text-pine-700 transition-colors hover:text-terra-500"
          >
            Questions residents are asking
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
};

export default GetInvolved;
